import React, { useState, useEffect, useRef } from 'react';
import {
  Actions,
  Container,
  LinkButton,
  Main,
  ReportContent,
  Title,
  ActionRow,
  ActionRowLeft,
  ActionIcon,
  ActionText,
  ActionTitle,
  ActionChevron,
} from './reportReview.style';
import { useNavigate } from 'react-router-dom';
import { getReport, getReportstatus } from '../../../services/reportAndReview';
import { ChevronLeft, ChevronRight, Download, Mail, Printer, RefreshCw } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import ReportDisplay from './reportDisplay';
import { getItem } from '../../../helper/localStorageHelper';
import { LoadingScreen } from '../../../components/loadingScreen';
import { toastActions } from '../../../components/uielements/toast/toast';
import { sentApplicationReportViaEmail } from '../../../services/application';
import { IntlMessages } from '../../../helper/langHelper';
import useOverlayStore from '../../../store/overlayStore';
import useShapeStore from '../../../store/shapeStore';
import ErrorState from '../../../components/state/errorState/error';

const PreLodgementReport = () => {
  const navigate = useNavigate();
  const [reportStatus, setReportStatus] = useState(false);
  const [reportContent, setReportContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [nextRefreshIn, setNextRefreshIn] = useState(30);

  const applicationID = getItem('applicationId');
  const reportRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const { clearOverlayData } = useOverlayStore();
  const { resetAll } = useShapeStore();

  // Auto-refresh configuration - Simple 5-second polling
  const POLL_INTERVAL = 5000; // 5 seconds
  const MAX_POLL_ATTEMPTS = 30; // Maximum 30 attempts (2.5 minutes total)
  const pollAttemptsRef = useRef(0);

  const handleSentViaEmail = async () => {
    const payload = {
      applicationId: applicationID,
      reportType: 'PR',
    };

    try {
      setIsEmailLoading(true);
      await sentApplicationReportViaEmail(payload);
      toastActions.success(<IntlMessages id="toast.email.success" />);
    } catch (error) {
      toastActions.error(<IntlMessages id="toast.email.failure" />);
    } finally {
      setIsEmailLoading(false);
    }
  };

  const checkReportStatus = async (isAutoRefresh = false) => {
    try {
      if (!isAutoRefresh) {
        setRefreshing(true);
      }

      const applicationId = applicationID || '680d12f978f576c982d24af1';
      const statusResponse = await getReportstatus({
        applicationId,
        reportType: 'PRELODGEMENT_REPORT',
      });

      setReportStatus(statusResponse.reportGenerationStatus);

      if (statusResponse.reportGenerationStatus) {
        const reportData = await getReport({ applicationId, reportType: 'PRELODGEMENT_REPORT' });
        setReportContent(reportData);

        // Stop polling once report is ready
        stopPolling();

        if (isAutoRefresh) {
          toastActions.success(<IntlMessages id="toast.report.ready" />);
        }
      } else {
        pollAttemptsRef.current += 1;

        // Stop polling after max attempts
        if (pollAttemptsRef.current >= MAX_POLL_ATTEMPTS) {
          stopPolling();
          toastActions.info(<IntlMessages id="toast.report.taking.longer" />);
        }
      }
    } catch (err) {
      setError(<IntlMessages id="error.fetch.report.status" />);
      stopPolling();
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const startPolling = () => {
    // Clear any existing intervals
    stopPolling();

    // Start polling every 5 seconds
    pollIntervalRef.current = setInterval(async () => {
      await checkReportStatus(true);
    }, POLL_INTERVAL);

    // Start countdown timer (updates every second)
    setNextRefreshIn(5);
    countdownIntervalRef.current = setInterval(() => {
      setNextRefreshIn((prev) => {
        if (prev <= 1) {
          return 5; // Reset to 5 seconds
        }
        return prev - 1;
      });
    }, 1000);
  };

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const toggleAutoRefresh = () => {
    setAutoRefreshEnabled((prev) => {
      const newValue = !prev;
      if (newValue && !reportStatus) {
        startPolling();
      } else {
        stopPolling();
      }
      return newValue;
    });
  };

  useEffect(() => {
    checkReportStatus();
  }, []);

  useEffect(() => {
    // Start auto-refresh ONLY if report is not ready and auto-refresh is enabled
    if (!reportStatus && autoRefreshEnabled && !loading) {
      startPolling();
    }

    // Cleanup on unmount
    return () => {
      stopPolling();
    };
  }, [reportStatus, autoRefreshEnabled, loading]);

  const handleNavigateToSuccess = () => {
    navigate('/reviewAndLodge');
  };

  const handleNavigateToHome = () => {
    clearOverlayData();
    resetAll();
    navigate('/');
  };

  const handleRefresh = () => {
    pollAttemptsRef.current = 0; // Reset attempt counter on manual refresh
    setNextRefreshIn(5); // Reset countdown to 5 seconds
    checkReportStatus(false);
  };

  // Print report function
  const handlePrint = () => {
    window.print();
  };

  // Download as PDF function
  const handleDownloadPDF = async () => {
    if (downloading) return;

    try {
      setDownloading(true);
      const reportElement = reportRef.current;
      if (!reportElement) {
        throw new Error('Report content not found');
      }

      // Hide table of contents temporarily for PDF generation
      const tableOfContents = document.getElementById('table-of-contents');
      let originalDisplay = null;
      if (tableOfContents) {
        originalDisplay = tableOfContents.style.display;
        tableOfContents.style.display = 'none';
      }

      // Create a filename with date
      const date = new Date();
      const filename = `Pre-Lodgement-Report-${date.toISOString().split('T')[0]}.pdf`;

      const canvas = await html2canvas(reportElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true,
      });

      // Restore table of contents display
      if (tableOfContents && originalDisplay !== null) {
        tableOfContents.style.display = originalDisplay;
      }

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add new pages if content overflows
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(filename);
    } catch (err) {
      toastActions.error(<IntlMessages id="error.download.pdf" />);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingScreen loadingText={<IntlMessages id="loading.report" />} />
      </Container>
    );
  }

  if (error) {
    return (
      <>
        <ErrorState
          title={<IntlMessages id="error.title" />}
          message={error}
          buttonText={<IntlMessages id="error.retry" />}
          onRetry={() => window.location.reload()}
        />
      </>
    );
  }

  if (!reportStatus) {
    return (
      <Container>
        <Title>
          <IntlMessages id="report.inprogress.title" />
        </Title>
        <Main>
          <ReportContent>
            {/* Status and Refresh Controls */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '20px',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
              }}
            >
              <div style={{ flex: 1 }}>
                <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '16px' }}>
                  <IntlMessages id="report.inprogress.status" />
                </p>
              </div>

              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                {/* Auto-refresh toggle */}
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '14px',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={autoRefreshEnabled}
                    onChange={toggleAutoRefresh}
                    style={{ marginRight: '6px', cursor: 'pointer' }}
                  />
                  Auto-refresh
                  {autoRefreshEnabled && (
                    <span
                      style={{
                        marginLeft: '8px',
                        fontSize: '12px',
                        color: '#666',
                      }}
                    >
                      (next in {nextRefreshIn}s)
                    </span>
                  )}
                </label>

                {/* Manual refresh button */}
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  aria-label={refreshing ? 'Refreshing report status' : 'Refresh report status'}
                  aria-busy={refreshing}
                  onKeyDown={(e) => {
                    if (e.key === ' ') {
                      e.preventDefault();
                      if (!refreshing) handleRefresh();
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '14px',
                    color: '#0066cc',
                    cursor: refreshing ? 'default' : 'pointer',
                    background: 'white',
                    border: '1px solid #0066cc',
                    borderRadius: '6px',
                    padding: '6px 12px',
                    opacity: refreshing ? 0.7 : 1,
                    fontFamily: 'JetBrains Mono Regular',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (!refreshing) {
                      e.currentTarget.style.backgroundColor = '#0066cc';
                      e.currentTarget.style.color = 'white';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.color = '#0066cc';
                  }}
                >
                  <RefreshCw
                    size={16}
                    style={{
                      marginRight: '6px',
                      animation: refreshing ? 'spin 1s linear infinite' : 'none',
                    }}
                    aria-hidden="true"
                  />
                  {refreshing ? (
                    <IntlMessages id="button.refreshing" />
                  ) : (
                    <IntlMessages id="button.refresh.status" />
                  )}
                </button>
              </div>
            </div>

            {/* Information Messages */}
            <div
              style={{
                padding: '20px',
                backgroundColor: '#e3f2fd',
                borderRadius: '8px',
                border: '1px solid #90caf9',
                marginBottom: '15px',
              }}
            >
              <p style={{ margin: '0 0 12px 0' }}>
                <IntlMessages id="report.inprogress.desc1" />
              </p>
              <p style={{ margin: '0 0 12px 0' }}>
                <IntlMessages id="report.inprogress.desc2" />
              </p>
            </div>

            <p style={{ textAlign: 'center', fontSize: '15px', color: '#555' }}>
              <IntlMessages id="report.inprogress.thanks" />
            </p>
          </ReportContent>
        </Main>
        <Actions>
          <LinkButton onClick={handleNavigateToHome}>
            <ChevronLeft size={18} />
            <IntlMessages id="button.back.dashboard" />
          </LinkButton>
        </Actions>

        <style>{`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </Container>
    );
  }

  return (
    <Container>
      <Main>
        <ReportContent id="pre-lodgement-report" ref={reportRef}>
          <ReportDisplay reportData={reportContent} />
        </ReportContent>
      </Main>

      <Actions>
        {/* <ActionRow onClick={handleNavigateToSuccess}>
          <ActionRowLeft>
            <ActionIcon>
              <FileEdit size={18} />
            </ActionIcon>
            <ActionText>
              <ActionTitle>
                <IntlMessages id="button.lodge" />
              </ActionTitle>
            </ActionText>
          </ActionRowLeft>
          <ActionChevron>
            <ChevronRight size={18} />
          </ActionChevron>
        </ActionRow> */}

        <ActionRow onClick={handlePrint}>
          <ActionRowLeft>
            <ActionIcon>
              <Printer size={18} />
            </ActionIcon>
            <ActionText>
              <ActionTitle>
                <IntlMessages id="button.print" />
              </ActionTitle>
            </ActionText>
          </ActionRowLeft>
          <ActionChevron>
            <ChevronRight size={18} />
          </ActionChevron>
        </ActionRow>

        <ActionRow onClick={handleSentViaEmail} disabled={isEmailLoading}>
          <ActionRowLeft>
            <ActionIcon>
              <Mail size={18} />
            </ActionIcon>
            <ActionText>
              <ActionTitle>
                {isEmailLoading ? (
                  <IntlMessages id="button.send.email.loading" />
                ) : (
                  <IntlMessages id="button.send.email" />
                )}
              </ActionTitle>
            </ActionText>
          </ActionRowLeft>
          <ActionChevron>
            <ChevronRight size={18} />
          </ActionChevron>
        </ActionRow>

        <ActionRow onClick={handleDownloadPDF} disabled={downloading}>
          <ActionRowLeft>
            <ActionIcon>
              <Download size={18} />
            </ActionIcon>
            <ActionText>
              <ActionTitle>
                {downloading ? (
                  <IntlMessages id="button.download.pdf.loading" />
                ) : (
                  <IntlMessages id="button.download.pdf" />
                )}
              </ActionTitle>
            </ActionText>
          </ActionRowLeft>
          <ActionChevron>
            <ChevronRight size={18} />
          </ActionChevron>
        </ActionRow>

        <LinkButton onClick={handleNavigateToHome}>
          <ChevronLeft size={18} />
          <IntlMessages id="button.back.dashboard" />
        </LinkButton>
      </Actions>

      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #pre-lodgement-report,
          #pre-lodgement-report * {
            visibility: visible;
          }
          #pre-lodgement-report {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 20px;
          }
          .Actions,
          button {
            display: none !important;
          }
          #table-of-contents {
            display: none !important;
          }
        }
        
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </Container>
  );
};

export default PreLodgementReport;
